import { useSpring } from "@react-spring/web";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";

import {
  ACTIVITY_MODAL_EXPIRED_TIME,
  TranslationContext,
  USER_ACTIVITY_EXPIRED_TIME,
} from "@is/constants";
import { UserActivityContext } from "@is/contexts/UserActivityContext";
import { useConfiguration, useStartNewSearch } from "@is/hooks";

const useManageUserActivityModal = () => {
  const { t } = useTranslation(TranslationContext.General);
  const { restartSearch } = useStartNewSearch();

  const {
    showUserActivityModal,
    setShowUserActivityModal,
    setUserActivityModalExpiredTime,
  } = useContext(UserActivityContext);

  const { timeouts } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const isFullExpiredTimeZero = useMemo(() => {
    if (!timeouts) {
      return false;
    }

    return (
      parseInt(timeouts.idle.minutes) === 0 &&
      parseInt(timeouts.idle.seconds) === 0 &&
      parseInt(timeouts.loadingBar.minutes) === 0 &&
      parseInt(timeouts.loadingBar.seconds) === 0
    );
  }, [timeouts]);

  const activityModalExpiredTime = useMemo(() => {
    if (!timeouts || isFullExpiredTimeZero) {
      return ACTIVITY_MODAL_EXPIRED_TIME;
    }

    return (
      (parseInt(timeouts.loadingBar.minutes) || 0) * 60 * 1000 +
      (parseInt(timeouts.loadingBar.seconds) || 0) * 1000
    );
  }, [isFullExpiredTimeZero, timeouts]);

  const userActivityExpiredTime = useMemo(() => {
    if (!timeouts || isFullExpiredTimeZero) {
      return USER_ACTIVITY_EXPIRED_TIME;
    }

    return (
      (parseInt(timeouts.idle.minutes) || 0) * 60 * 1000 +
      (parseInt(timeouts.idle.seconds) || 0) * 1000 +
      activityModalExpiredTime
    );
  }, [activityModalExpiredTime, isFullExpiredTimeZero, timeouts]);

  const [animatedRef, { width }] = useMeasure();
  const styles = useSpring({
    width: showUserActivityModal ? width : 0,
    config: { duration: activityModalExpiredTime },
  });

  const handleContinueSearch = useCallback(() => {
    const newExpiredTime = Date.now() + userActivityExpiredTime;

    setShowUserActivityModal(false);
    setUserActivityModalExpiredTime(newExpiredTime);
  }, [
    userActivityExpiredTime,
    setShowUserActivityModal,
    setUserActivityModalExpiredTime,
  ]);

  const handleResetUserActivity = useCallback(async () => {
    const newExpiredTime = Date.now() + userActivityExpiredTime;

    setShowUserActivityModal(false);
    setUserActivityModalExpiredTime(newExpiredTime);

    await restartSearch();
  }, [
    userActivityExpiredTime,
    setShowUserActivityModal,
    setUserActivityModalExpiredTime,
    restartSearch,
  ]);

  const handleModalClose = useCallback(() => {
    const newExpiredTime = Date.now() + userActivityExpiredTime;

    setShowUserActivityModal(false);
    setUserActivityModalExpiredTime(newExpiredTime);
  }, [
    userActivityExpiredTime,
    setShowUserActivityModal,
    setUserActivityModalExpiredTime,
  ]);

  const handleModalClick = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    []
  );

  return {
    showUserActivityModal,
    handleContinueSearch,
    handleResetUserActivity,
    handleModalClick,
    handleModalClose,
    animatedRef,
    styles,
    t,
  };
};

export default useManageUserActivityModal;
